import { View } from '@react-pdf/renderer';

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import usePrintTemplate from 'hooks/usePrintTemplate';

import Barcode from './Barcode';
import ExpandedDescription from './ExpandedDescription';
import Image from './Image';
import RTLText from './RTLText';

const PrintTable = ({
	styles,
	variant = null,
	itemConfig,
	items,
	columnSettings,
	wrap = true,
	children,
	language,
	twoLanguage = false
}) => {
	const { getRows, getColumns } = usePrintTemplate(itemConfig);
	const { t } = useTranslation('translation', {
		lng: language
	});

	const columns = useMemo(() => getColumns(columnSettings), [columnSettings]);
	const rows = useMemo(() => getRows(items, columnSettings), [items, columnSettings]);

	const renderers = {
		text: (item, col, styles) => <RTLText style={styles}>{item[col.accessor]}</RTLText>,
		barcode: (item, col, styles) => (
			<Barcode
				value={item[col.accessor]}
				options={{
					format: 'CODE128',
					height: 40,
					displayValue: false,
					flat: true,
					margin: 0
				}}
				barcodeStyles={styles.barcode}
				textStyles={styles}
			/>
		),
		image: (item, col) => (
			<View
				style={{
					width: '100%',
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'flex-start',
					justifyContent: 'flex-start',
					marginTop: 5,
					marginBottom: 5
				}}>
				<Image styles={{ maxWidth: 50, maxHeight: 50 }} source={item[col.accessor]} />
			</View>
		),
		expandedDescription: (item, col, styles) => (
			<ExpandedDescription styles={styles} expandedDescription={item[col.accessor]} language={language} />
		)
	};

	const getStyles = (style, iteratorValues) => {
		if (!style) return {};
		if (typeof style === 'function') {
			return style(...iteratorValues);
		}
		return style;
	};

	return (
		<View style={styles?.table ?? {}} wrap={wrap}>
			{/** table header */}
			{/** Table Col Headers */}
			<View style={styles?.thead?.theadRow ?? {}} fixed>
				{columns.map((thead, i, arr) => (
					<View
						key={i}
						style={[
							getStyles(styles?.thead?.theadCol, [thead, i, arr]),
							{ width: `${thead?.width || 10}%` }
						]}>
						<RTLText style={getStyles(styles?.thead?.theadCell, [thead, i, arr])}>
							{twoLanguage && language !== 'arabicValue' && !['sku', 'skuBarcode'].includes(thead.name)
								? `${t(thead.name)}-${t(thead.name, { lng: 'arabicValue' })}`
								: t(thead.name)}
						</RTLText>
					</View>
				))}
			</View>
			{/** Table Col Headers */}
			{/** item renderer */}
			{/** Table Rows */}
			{rows?.map((item, index, arr) => (
				<View
					style={[
						getStyles(styles?.tbody?.tbodyRow, [item, index, arr]),
						variant === 'striped'
							? {
									backgroundColor: index % 2 === 0 ? '#f5f5f5' : '#fff'
								}
							: {}
					]}
					key={index}>
					{columns.map((col, i, array) => (
						<View
							style={[
								getStyles(styles?.tbody?.tbodyCol, [col, i, array]),
								{ width: `${col?.width || 10}%` }
							]}
							key={i}>
							{/* Renderer */}
							{renderers[col.renderCell](item, col, getStyles(styles?.tbody?.tbodyCell, [col, i, array]))}
							{/* Renderer */}
						</View>
					))}
				</View>
			))}
			{/** Table Rows */}
			{/** extra informations */}
			{!!children && children}
			{/** extra informations */}
		</View>
	);
};

export default PrintTable;
